import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

interface RevealState<T extends HTMLElement> {
  ref: React.RefObject<T | null>;
  revealed: boolean;
}

/**
 * Reveals an element once it scrolls into view, `delay` ms later.
 *
 * A zero threshold with a bottom root margin keeps this correct for elements
 * taller than the viewport, which a ratio threshold can never satisfy.
 */
export function useReveal<T extends HTMLElement>(delay = 0): RevealState<T> {
  const reduced = useReducedMotion();
  const ref = useRef<T>(null);
  // Seeded from `reduced` so motion-averse visitors never see a frame at opacity 0.
  const [revealed, setRevealed] = useState(reduced);

  useEffect(() => {
    const el = ref.current;

    if (reduced || !el || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return;
    }

    let timer: number | undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          timer = window.setTimeout(() => setRevealed(true), delay);
        });
      },
      { threshold: 0, rootMargin: '0px 0px -8% 0px' }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [delay, reduced]);

  return { ref, revealed };
}
